import { ROLE_LABELS, TASK_STATUS_LABELS } from "@onadssuper/validators";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  PageHeader,
  Spinner,
} from "../../../components/ui";
import { NextActionText } from "../../../features/campaigns/next-action";
import { CampaignStatusBadge } from "../../../features/campaigns/status-badge";
import { useCan, useCurrentWorkspace } from "../../../features/workspace/workspace-context";
import { useTRPC } from "../../../lib/trpc";
import { formatCalendarDate } from "../../../lib/utils";

export const Route = createFileRoute("/w/$slug/")({ component: WorkspaceHome });

function WorkspaceHome() {
  const workspace = useCurrentWorkspace();
  const canManageMembers = useCan("member.invite");
  const agency = useCan("task.move"); // held by every agency role and no client role
  const trpc = useTRPC();
  const home = useQuery(trpc.home.get.queryOptions());
  return (
    <>
      <PageHeader
        title={workspace.name}
        description="Your shared workspace for campaigns, reviews and approvals."
        actions={<Badge tone="primary">{ROLE_LABELS[workspace.role]}</Badge>}
      />
      {home.isPending ? (
        <Spinner className="size-4" />
      ) : home.isError ? (
        <ErrorState error={home.error} onRetry={() => void home.refetch()} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <HomeCard
            label={agency ? "Waiting on the agency" : "Waiting on the client"}
            empty="Nothing is waiting right now."
          >
            {home.data.waiting.map((w) => (
              <Row key={w.campaign.id} campaignId={w.campaign.id} name={w.campaign.name}>
                <NextActionText action={w.action} />
              </Row>
            ))}
          </HomeCard>
          <HomeCard
            label={agency ? "My campaigns" : "Campaigns"}
            empty={
              agency ? "You don't own or work on an open campaign yet." : "No open campaigns yet."
            }
            action={
              <Link
                to="/w/$slug/campaigns"
                params={{ slug: workspace.slug }}
                className="text-sm text-primary hover:underline"
              >
                All campaigns →
              </Link>
            }
          >
            {home.data.myCampaigns.map((c) => (
              <Row key={c.id} campaignId={c.id} name={c.name}>
                <span className="flex flex-wrap items-center gap-2">
                  <CampaignStatusBadge status={c.status} />
                  <NextActionText action={c.nextAction} />
                </span>
              </Row>
            ))}
          </HomeCard>
          {agency && (
            <HomeCard label="My tasks" empty="No open tasks assigned to you.">
              {home.data.myTasks.map((t) => (
                <Row key={t.id} campaignId={t.campaign.id} name={t.title}>
                  <span className="text-muted">
                    {t.campaign.name} · {TASK_STATUS_LABELS[t.status]}
                    {t.dueOn && ` · due ${formatCalendarDate(t.dueOn)}`}
                  </span>
                </Row>
              ))}
            </HomeCard>
          )}
          {canManageMembers && (
            <Card>
              <CardHeader title="People" />
              <CardBody className="text-sm">
                <Link
                  to="/w/$slug/members"
                  params={{ slug: workspace.slug }}
                  className="text-primary hover:underline"
                >
                  Invite people to this workspace →
                </Link>
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

const HomeCard = ({
  label,
  empty,
  action,
  children,
}: {
  label: string;
  empty: string;
  action?: ReactNode;
  children: ReactNode[];
}) => (
  <section aria-label={label}>
    <Card>
      <CardHeader title={label} actions={action} />
      <CardBody className="text-sm">
        {children.length === 0 ? (
          <p className="text-muted">{empty}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">{children}</ul>
        )}
      </CardBody>
    </Card>
  </section>
);

const Row = ({
  campaignId,
  name,
  children,
}: {
  campaignId: string;
  name: string;
  children: ReactNode;
}) => {
  const workspace = useCurrentWorkspace();
  return (
    <li className="flex flex-col gap-1 py-2">
      <Link
        to="/w/$slug/campaigns/$campaignId"
        params={{ slug: workspace.slug, campaignId }}
        className="truncate font-medium hover:text-primary hover:underline"
      >
        {name}
      </Link>
      {children}
    </li>
  );
};
